import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { NgxPaginationModule } from 'ngx-pagination';

import { SharedModule } from '@ubie/shared';
import { IonicSelectableComponent } from 'ionic-selectable';
import { ConfigurationPopoverComponent } from './components/configurations/configuration-popover/configuration-popover.component';
import { ConfigurationsByDevicesListItemComponent } from './components/configurations/configurations-by-devices-list-item/configurations-by-devices-list-item.component';
import { ConfigurationsByDevicesListComponent } from './components/configurations/configurations-by-devices-list/configurations-by-devices-list.component';
import { FavoritesListComponent } from './components/dashboard/favorites-list/favorites-list.component';
import { InfoBlockComponent } from './components/dashboard/info-block/info-block.component';
import { InvitationsListComponent } from './components/dashboard/invitations-list/invitations-list.component';
import { MessagesListComponent } from './components/dashboard/messages-list/messages-list.component';
import { SliderComponent } from './components/dashboard/slider/slider.component';
import { ActivationModalComponent } from './components/devices/activation-modal/activation-modal.component';
import { DeviceAddToInstallationComponent } from './components/devices/device-add-to-installation/device-add-to-installation.component';
import { DeviceDeleteComponent } from './components/devices/device-delete/device-delete.component';
import { DeviceInfoV1Component } from './components/devices/device-info-v1/device-info-v1.component';
import { DevicesByInstallationsListComponent } from './components/devices/devices-by-installations-list/devices-by-installations-list.component';
import { GeneralPopoverComponent } from './components/general/general-popover/general-popover.component';
import { InstallationCtdLimitComponent } from './components/installations/installation-ctd-limit/installation-ctd-limit.component';
import { InstallationDeliveryStatusV1Component } from './components/installations/installation-delivery-status-v1/installation-delivery-status-v1.component';
import { InstallationFormV1Component } from './components/installations/installation-form-v1/installation-form-v1.component';
import { InstallationInstallerItemComponent } from './components/installations/installation-installer-item/installation-installer-item.component';
import { InstallationInstallerComponent } from './components/installations/installation-installer/installation-installer.component';
import { InstallationInvitationComponent } from './components/installations/installation-invitation/installation-invitation.component';
import { InstallationListComponent } from './components/installations/installation-list/installation-list.component';
import { InstallationNoAccessComponent } from './components/installations/installation-no-access/installation-no-access.component';
import { InstallationPaymentComponent } from './components/installations/installation-payment/installation-payment.component';
import { InstallationSubNavComponent } from './components/installations/installation-sub-nav/installation-sub-nav.component';
import { InstallationTypeComponent } from './components/installations/installation-type/installation-type.component';
import { InstallerModalComponent } from './components/installations/installer-modal/installer-modal.component';
import { OrderStatusComponent } from './components/orders/order-status/order-status.component';
import { OwnerDeleteModalComponent } from './components/owner/owner-delete-modal/owner-delete-modal.component';
import { OwnerFormComponent } from './components/owner/owner-form/owner-form.component';
import { OwnerModalComponent } from './components/owner/owner-modal/owner-modal.component';
import { ForgotPasswordV2Component } from './components/password/forgot-password-v2/forgot-password-v2.component';
import { ProductsListComponent } from './components/products/products-list/products-list.component';
import { PurchasesListComponent } from './components/products/purchases-list/purchases-list.component';
import { AccountConfirmComponent } from './components/registration/account-confirm/account-confirm.component';
import { AccountDetailsComponent } from './components/registration/account-details/account-details.component';
import { ActivateUserComponent } from './components/registration/activate-user/activate-user.component';
import { RegisterCompanyDetailsComponent } from './components/registration/register-company-details/register-company-details.component';
import { RegisterContactDetailsComponent } from './components/registration/register-contact-details/register-contact-details.component';
import { RegisterProfessionalDetailsComponent } from './components/registration/register-professional-details/register-professional-details.component';
import { RegisterSuccessfulComponent } from './components/registration/register-successful/register-successful.component';
import { RegistrationComponent } from './components/registration/registration/registration.component';
import { AddDeviceModalComponent } from './components/shared/add-device-modal/add-device-modal.component';
import { CardHeaderComponent } from './components/shared/card-header/card-header.component';
import { ConfirmModalComponent } from './components/shared/confirm-modal/confirm-modal.component';
import { ErrorModalComponent } from './components/shared/error-modal/error-modal.component';
import { VatComponent } from './components/shared/forms/vat/vat.component';
import { TimeAgoPipe } from './components/shared/pipes/time-ago/time-ago.pipe';
import { SuccessModalComponent } from './components/shared/success-modal/success-modal.component';
import { PasswordFormComponent } from './components/user/password-form/password-form.component';
import { UserFormComponent } from './components/user/user-form-component/user-form-component.component';

@NgModule({
  declarations: [
    OrderStatusComponent,
    OwnerFormComponent,
    OwnerModalComponent,
    OwnerDeleteModalComponent,
    SliderComponent,
    MessagesListComponent,
    InfoBlockComponent,
    FavoritesListComponent,
    InvitationsListComponent,
    InstallationListComponent,
    InstallationCtdLimitComponent,
    InstallationNoAccessComponent,
    InstallationSubNavComponent,
    InstallationPaymentComponent,
    ProductsListComponent,
    PurchasesListComponent,
    ConfirmModalComponent,
    ErrorModalComponent,
    SuccessModalComponent,
    GeneralPopoverComponent,
    InstallationTypeComponent,
    InstallationDeliveryStatusV1Component,
    InstallationFormV1Component,
    InstallationInvitationComponent,
    InstallationInstallerComponent,
    InstallationInstallerItemComponent,
    InstallerModalComponent,
    CardHeaderComponent,
    VatComponent,
    DevicesByInstallationsListComponent,
    ActivationModalComponent,
    DeviceInfoV1Component,
    DeviceDeleteComponent,
    DeviceAddToInstallationComponent,
    AccountConfirmComponent,
    AccountDetailsComponent,
    ActivateUserComponent,
    RegisterCompanyDetailsComponent,
    RegisterContactDetailsComponent,
    RegisterProfessionalDetailsComponent,
    RegisterSuccessfulComponent,
    RegistrationComponent,
    UserFormComponent,
    PasswordFormComponent,
    ConfigurationsByDevicesListComponent,
    ConfigurationsByDevicesListItemComponent,
    ConfigurationPopoverComponent,
    ForgotPasswordV2Component,
    AddDeviceModalComponent,
  ],
  exports: [
    OrderStatusComponent,
    OwnerFormComponent,
    OwnerModalComponent,
    OwnerDeleteModalComponent,
    SliderComponent,
    MessagesListComponent,
    InfoBlockComponent,
    FavoritesListComponent,
    InvitationsListComponent,
    InstallationListComponent,
    InstallationCtdLimitComponent,
    InstallationNoAccessComponent,
    InstallationSubNavComponent,
    InstallationSubNavComponent,
    InstallationDeliveryStatusV1Component,
    InstallationFormV1Component,
    CardHeaderComponent,
    NgxPaginationModule,
    UserFormComponent,
    PurchasesListComponent,
    PasswordFormComponent,
    ForgotPasswordV2Component,
    RegistrationComponent,
    IonicSelectableComponent,
    DeviceAddToInstallationComponent,
    DevicesByInstallationsListComponent,
    ConfigurationPopoverComponent,
    InstallationPaymentComponent,
    ConfirmModalComponent,
    ErrorModalComponent,
    SuccessModalComponent,
    GeneralPopoverComponent,
    DeviceDeleteComponent,
    InstallationTypeComponent,
    DeviceInfoV1Component,
    ConfigurationsByDevicesListComponent,
    AddDeviceModalComponent,
    InstallationInvitationComponent,
    InstallationInstallerComponent,
    InstallationInstallerItemComponent,
    ActivateUserComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    IonicModule.forRoot({
      useSetInputAPI: true,
    }),
    NgxPaginationModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    TimeAgoPipe,
    IonicSelectableComponent,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AdminCoreLibModule {}
